import { Launch as IconExport } from "@mui/icons-material";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import _isArray from "lodash/isArray";
import { accessTokenKey } from "misc/authProvider";
import { useExportFieldsets } from "misc/customHooks";
import { exportPath } from "misc/customPaths";
import { defaultListFetchParams } from "misc/edmClient";
import {
  CustomExportFieldsByResource,
  enhanceWithCustomExportFields,
  eventFieldsets,
  ExportFieldsets,
  exportFieldsForCustomFieldContentType,
  getAllPriceTypesFieldset,
  getPriceTypeFieldset,
} from "misc/exportFieldsets";
import { apiEndpoint } from "misc/settings";
import { CustomFieldContentTypeRecordType } from "pages/CustomField/CustomField.types";
import { EventRecordType } from "pages/Event/Event.types";
import { PriceTypeRecordType } from "pages/SharedRecords.types";
import { Fragment, useEffect, useState } from "react";
import { TranslateFunction, useDataProvider, useTranslate } from "react-admin";

import EdmButton from "../../atoms/Buttons/EdmButton";
import CheckboxGroups from "./CheckboxGroups";

type ExportDialogPropsType = {
  selectedIds?: number[];
  fieldsets: (
    translate: TranslateFunction,
    includeLecturerDetails?: boolean,
    additionalFieldsets?: ExportFieldsets,
  ) => ExportFieldsets;
  descriptionFieldsets?: ExportFieldsets | null;
  filters: { [key: string]: string | string[] };
  resource: string;
  relatedEventId?: number | null;
  disableCustomFields?: boolean;
};

const SELECTION_THRESHOLD = 100;

export default function ExportDialog(props: ExportDialogPropsType) {
  const {
    selectedIds,
    descriptionFieldsets: initialDescriptionFieldsets = null,
    filters,
    resource,
    relatedEventId = null,
    disableCustomFields = false,
  } = props;
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const [customExportFieldsByResource, setCustomExportFieldsByResource] = useState<CustomExportFieldsByResource | null>(
    null,
  );
  const [relatedEvent, setRelatedEvent] = useState<EventRecordType | null>(null);
  const [selectedEvents, setSelectedEvents] = useState<EventRecordType[]>([]);
  const [allPriceTypes, setAllPriceTypes] = useState<PriceTypeRecordType[]>([]);

  const fetchCustomExportFields = async () => {
    const fetchParams = { ...defaultListFetchParams };
    const result = await dataProvider.getList<CustomFieldContentTypeRecordType>(
      "custom_field_content_types",
      fetchParams,
    );

    const customExportFieldsByResource: CustomExportFieldsByResource = {};
    result.data.forEach((group) => {
      customExportFieldsByResource[`${group.resource}s`] = exportFieldsForCustomFieldContentType(group);
    });

    setCustomExportFieldsByResource(customExportFieldsByResource);
  };

  useEffect(() => {
    if (!disableCustomFields) {
      fetchCustomExportFields().catch(() => setCustomExportFieldsByResource(null));
    }
  }, []);

  useEffect(() => {
    const fetchAndSetRelatedEvent = async () => {
      if (!relatedEventId) return setRelatedEvent(null);

      try {
        const res = await dataProvider.getOne<EventRecordType>("events", { id: relatedEventId });
        setRelatedEvent(res.data);
      } catch {
        setRelatedEvent(null);
      }
    };

    fetchAndSetRelatedEvent();
  }, [relatedEventId]);

  useEffect(() => {
    const fetchAndSetSelectedEvents = async () => {
      try {
        // Check if we should skip fetching events due to selection size
        if (selectedIds && selectedIds.length > SELECTION_THRESHOLD) {
          setSelectedEvents([]);
          return;
        }

        if (selectedIds && selectedIds.length > 0) {
          const res = await dataProvider.getList<EventRecordType>("events", {
            ...defaultListFetchParams,
            filter: { id__in: selectedIds },
          });
          setSelectedEvents(res.data);
        }
      } catch {
        setSelectedEvents([]);
      }
    };

    if (resource === "events") {
      fetchAndSetSelectedEvents();
    } else {
      setSelectedEvents([]);
    }
  }, [selectedIds?.length, resource]);

  // Fetch all price types when threshold is exceeded
  useEffect(() => {
    const fetchAllPriceTypes = async () => {
      if (resource !== "events" || !selectedIds || selectedIds.length <= SELECTION_THRESHOLD) {
        return;
      }
      try {
        const res = await dataProvider.getList<PriceTypeRecordType>("price_types", {
          pagination: { page: 1, perPage: 1000 },
          sort: { field: "name", order: "ASC" },
          filter: {},
        });
        setAllPriceTypes(res.data);
      } catch {
        setAllPriceTypes([]);
      }
    };

    fetchAllPriceTypes();
  }, [selectedIds?.length, resource]);

  let initialRelatedEventFieldsets = null;
  if (relatedEvent) {
    const includeLecturerDetails =
      relatedEvent.lecturers.length === 1 && (resource === "bookings" || resource === "attendees");
    const pricesFieldset = getPriceTypeFieldset([relatedEvent], translate);
    initialRelatedEventFieldsets = eventFieldsets(translate, includeLecturerDetails, pricesFieldset);
    initialRelatedEventFieldsets = enhanceWithCustomExportFields(
      initialRelatedEventFieldsets,
      "events",
      customExportFieldsByResource,
      translate,
    );
  }

  let fieldsets;

  const exceedsThreshold = selectedIds && selectedIds.length > SELECTION_THRESHOLD;

  if (resource === "events") {
    if (exceedsThreshold) {
      const pricesFieldset = getAllPriceTypesFieldset(allPriceTypes, translate);
      fieldsets = props.fieldsets(translate, false, pricesFieldset);
    } else {
      const includeLecturerDetails = selectedEvents.every((event) => event.lecturers.length === 1);
      const pricesFieldset = getPriceTypeFieldset(selectedEvents, translate);
      fieldsets = props.fieldsets(translate, includeLecturerDetails, pricesFieldset);
    }
  } else {
    fieldsets = props.fieldsets(translate);
  }

  fieldsets = enhanceWithCustomExportFields(fieldsets, resource, customExportFieldsByResource, translate);

  const {
    fieldsets: directFieldsets,
    selection: directFieldsSelection,
    handleClickFieldset: handleDirectFieldsetClick,
    handleClickField: handleDirectFieldClick,
  } = useExportFieldsets(fieldsets);
  const {
    fieldsets: relatedEventFieldsets,
    selection: relatedEventFieldsSelection,
    handleClickFieldset: handleRelatedEventFieldsetClick,
    handleClickField: handleRelatedEventFieldClick,
  } = useExportFieldsets(initialRelatedEventFieldsets);
  const {
    fieldsets: descriptionFieldsets,
    selection: descriptionFieldsSelection,
    handleClickFieldset: handleDescriptionFieldsetClick,
    handleClickField: handleDescriptionFieldClick,
  } = useExportFieldsets(initialDescriptionFieldsets);

  const csrfToken = document.cookie
    .split("; ")
    .find((row) => row.startsWith("csrftoken="))
    ?.split("=")[1];

  return (
    <Fragment>
      <DialogContent>
        <CheckboxGroups
          groupsData={directFieldsets}
          handleClickGroup={handleDirectFieldsetClick}
          handleClickChild={handleDirectFieldClick}
        />
        {descriptionFieldsets && (
          <CheckboxGroups
            groupsData={descriptionFieldsets}
            handleClickGroup={handleDescriptionFieldsetClick}
            handleClickChild={handleDescriptionFieldClick}
          />
        )}
        {relatedEventId && (
          <CheckboxGroups
            groupsData={relatedEventFieldsets}
            handleClickGroup={handleRelatedEventFieldsetClick}
            handleClickChild={handleRelatedEventFieldClick}
          />
        )}
      </DialogContent>

      <form action={`${apiEndpoint}${exportPath(`${resource}/`)}`} method="post">
        {selectedIds && selectedIds.map((id) => <input key={id} name="ids" type="hidden" value={id} />)}
        {directFieldsSelection.fields.map((field) => (
          <input key={field} name="fields" type="hidden" value={field} />
        ))}
        {directFieldsSelection.translations.map((translatedField) => (
          <input key={translatedField} name="translated_fields" type="hidden" value={translatedField} />
        ))}
        {filters &&
          Object.keys(filters).map((filterName) => {
            const value = filters[filterName];
            if (_isArray(value)) {
              return (
                <Fragment key={filterName}>
                  {value.map((v) => (
                    <input key={v} name={filterName} type="hidden" value={v} />
                  ))}
                </Fragment>
              );
            } else {
              return <input key={filterName} name={filterName} type="hidden" value={value} />;
            }
          })}
        {relatedEventId && <input name="related_event_id" type="hidden" value={relatedEventId} />}
        {relatedEventFieldsSelection.fields.map((field) => (
          <input key={field} name="related_event_fields" type="hidden" value={field} />
        ))}
        {relatedEventFieldsSelection.translations.map((field) => (
          <input key={field} name="translated_related_event_fields" type="hidden" value={field} />
        ))}

        {descriptionFieldsSelection.fields.map((field) => (
          <input key={field} name="event_base_description_slugs" type="hidden" value={field} />
        ))}
        {descriptionFieldsSelection.translations.map((field) => (
          <input key={field} name="event_base_description_labels" type="hidden" value={field} />
        ))}
        <input name="auth_token" type="hidden" value={localStorage.getItem(accessTokenKey) || ""} />
        <input name="csrfmiddlewaretoken" type="hidden" value={csrfToken} />

        <DialogActions>
          <EdmButton
            label="ra.action.export"
            type="submit"
            disabled={directFieldsSelection.fields.length === 0 || (selectedIds && selectedIds.length === 0)}
            color="primary"
            variant="contained"
            data-testid="export_items"
          >
            <IconExport />
          </EdmButton>
        </DialogActions>
      </form>
    </Fragment>
  );
}
